import React from 'react';

import ToastContext from './ToastContext';
import ToastStack from './ToastStack';
import { ToastStackProps } from './ToastStack.common';
import { useToastList } from './useToast';

export type ToastProviderProps = Pick<ToastStackProps, 'placement' | 'maxVisible'> & {
  children: React.ReactNode;
};

/**
 * Owns the toasts for an app and renders the one stack they appear in, so a screen only has to
 * call `useToast()` and show one. Mounted at the app root; a second one nested inside would give
 * its subtree a stack of its own.
 *
 * The stack renders after `children` rather than around them: on native it has to be a sibling of
 * the screen's content for the overlay to pin to the root.
 */
const ToastProvider = ({ children, placement, maxVisible }: ToastProviderProps): JSX.Element => {
  const toast = useToastList();

  // The deprecated single-toast fields, answered as "nothing for you to render": a screen still
  // on that path draws no toast of its own, and its messages come up in this stack instead.
  const value = React.useMemo(
    () => ({ ...toast, isToastVisible: false, toastProperties: undefined }),
    [toast]
  );

  return (
    <>
      <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
      {/* Outside the context, so this stack is not mistaken for one a screen drew for itself. */}
      <ToastStack {...toast.toastStackProps} placement={placement} maxVisible={maxVisible} />
    </>
  );
};

export default ToastProvider;
